import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { defineMessages } from 'react-intl';

export enum LoadingState {
	done,
	initial,
	pagination,
}

export enum HomeViewFilters {
	ALL = 'ALL',
	WORKED_ON = 'WORKED_ON',
	CREATED = 'CREATED',
	STARRED = 'STARRED',
	TASKS = 'TASKS',
	DRAFTS = 'DRAFTS',
}

export enum ActivityEventType {
	ASSIGNED = 'ASSIGNED',
	COMMENTED = 'COMMENTED',
	CREATED = 'CREATED',
	EDITED = 'EDITED',
	LIKED = 'LIKED',
	PUBLISHED = 'PUBLISHED',
	SNAPSHOTTED = 'SNAPSHOTTED',
	STARTED = 'STARTED',
	TRANSITIONED = 'TRANSITIONED',
	UNASSIGNED = 'UNASSIGNED',
	UPDATED = 'UPDATED',
	VIEWED = 'VIEWED',
}

export enum ActivitiesObjectType {
	BLOGPOST = 'BLOGPOST',
	DATABASE = 'DATABASE',
	EMBED = 'EMBED',
	ISSUE = 'ISSUE',
	PAGE = 'PAGE',
	SLIDE = 'SLIDE',
	WHITEBOARD = 'WHITEBOARD',
	CONTENT = 'CONTENT',
	COMMENT = 'COMMENT',
}

const i18n = defineMessages({
	untitled: {
		id: 'home.views.untitled',
		defaultMessage: 'Untitled',
		description: 'Title displayed for a page that does not have a title',
	},
});
export const untitled: {
	id: string;
	defaultMessage: string;
	description: string;
} = i18n.untitled;

export const getLoadingState = (networkStatus: NetworkStatus): LoadingState => {
	if (networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables) {
		return LoadingState.initial;
	}
	if (networkStatus === NetworkStatus.fetchMore) {
		return LoadingState.pagination;
	}
	return LoadingState.done;
};

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
	return value !== null && value !== undefined;
}

export const byTitle = (textFilter: string, title?: string | null): boolean => {
	const trimmedTextFilter = textFilter.trim();
	if (trimmedTextFilter === '') {
		return true;
	}

	return (title || '').toUpperCase().indexOf(textFilter.toUpperCase()) > -1;
};

export const transformActivityData = (recent: any, isActivityV3HomeRecent: any): any => {
	if (recent?.object?.data?.commentId) {
		recent.event.eventType = 'commented';
		recent.object.data = recent?.object?.data?.container;
	}

	const content = recent?.object?.data;

	if (isActivityV3HomeRecent) {
		if (content) {
			content.type = content.type?.toLowerCase();

			if (content.type === 'blog_post') {
				content.type = 'blogpost';
			}

			content.subtype = content.subtype?.toLowerCase();

			// The "status" field is ConfluencePage/ConfluenceBlogPost type for pages/blogs but is
			// ConfluenceContentStatus type for all other content types. To prevent the GraphQL
			// "returns different types" error, we are using the aliases "pageStatus" and "blogStatus"
			content.status = [content.status, content.pageStatus, content.blogStatus]
				.find((status) => status)
				?.toLowerCase();

			// FE workaround for activity query returning "status: DRAFT" for published content with unpublished changes
			if (content.status === 'draft' && content.createdAt) {
				content.status = 'current';
			}
		}

		recent.event.eventType = recent.event?.eventType.toUpperCase();
	}

	return recent;
};

export const notArchived = (content: any): boolean => {
	return content?.status !== 'archived';
};

export const notDeleted = (content: any): boolean => {
	return content?.status !== 'trashed';
};

export const isCreatorCurrentUser = (userId: string | null, content: any): boolean => {
	const createdBy =
		content?.status === 'draft' ? content?.version?.by : content?.history?.createdBy;
	return userId === createdBy?.accountId;
};
